import { ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { IOrdersHistoryProps } from "./ordersHistory";
import { useGetCompanyByIdQuery, useLazyGetCompanyFilteredOrdersQuery, useLazyGetCompanyOrderByIdQuery } from "../../../../../../API/authApi";
import { IFilteredOrdersForm, OrderStatus } from "../../../../../../Interfaces/formValue-interfaces";
import { CurrentOrdersContentTypes, IAutocompleteData, OrderTypes } from "../../../../../../Interfaces/componentTypes";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";
import { IOrderResponse } from "../../../../../../Interfaces/responseTypes";
import useDialogHooks from "../../../../../../Components/Dialog/dialog-hooks";
import OrderStatusDialog from "../../Dialogs/OrderStatusDialog/orderStatusDialog";
import PrintInvoiceDialog from "../../Dialogs/PrintInvoice/printInvoiceDialog";
import { IGetCompanyFileredData } from "../../../../../../Interfaces/request-interfaces";
import { getActiveDineInUnits } from "../../Services/Contents/MyServices/DineInUnits/dineInHelpers";

const useOrdersHistory = (props: IOrdersHistoryProps) => {
    const { company_id, QR } = props;
    const { t } = useLanguagesHooks();

    const [isFilterBox, setIsFilterBox] = useState<boolean>(false);
    const [filterdOrders, setFilterdOrders] = useState<IOrderResponse[]>([]);
    const [nextPage, setNextPage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [ordersCount, setOrdersCount] = useState(0);
    const [activeContent, setActiveContent] = useState<CurrentOrdersContentTypes>(CurrentOrdersContentTypes.LIST);
    const [selectedOrder, setSelectedOrder] = useState<IOrderResponse | null>(null);

    const { open: isDialogOpen, onOpen, onClose } = useDialogHooks();
    const [dialogComponent, setDialogComponent] = useState<ReactNode | null>(null);

    const [getOrderById, { data: orderByIdData }] = useLazyGetCompanyOrderByIdQuery();
    const [getFilteredOrders] = useLazyGetCompanyFilteredOrdersQuery();
    const { data: companyData } = useGetCompanyByIdQuery({ id: company_id });

    const { register, getValues, control, setValue, setError, watch, formState: { errors } } = useForm<IFilteredOrdersForm>();
    const orderId = watch("order_id");
    const selectedOrderType = watch("order_type");
    const isDineInFilter = selectedOrderType?.id === OrderTypes.DINE_IN;

    const dineInLocationTypes: IAutocompleteData = useMemo(() => {
        const units = getActiveDineInUnits(companyData?.services?.dine_in?.units ?? []);
        const uniqueTypes = [...new Set(units.map((unit) => unit.type))];

        return uniqueTypes.map((type) => ({
            id: type,
            title: t(`My_Business.My_Services.Dine_In.Unit_Types.${type}`),
        }));
    }, [companyData?.services?.dine_in?.units, t]);

    const hasMultipleLocationTypes = dineInLocationTypes.length > 1;
    const singleLocationType = dineInLocationTypes.length === 1 ? dineInLocationTypes[0] : null;

    /** 🔎 search by ID */
    const onSearchById = () => {
        if (orderId) {
            getOrderById({ order_id: orderId, company_id }).unwrap()
                .then((data) => {
                    setFilterdOrders([data]);
                    setNextPage(null);
                    setIsFilterBox(false);
                })
                .catch((err) => setError('order_id', { message: "Error" }));
        }
    };

    const appendDineInLocationFilters = (
        payload: IGetCompanyFileredData,
        values: IFilteredOrdersForm,
    ) => {
        if (values.order_type?.id !== OrderTypes.DINE_IN) {
            return;
        }

        const locationType = values.location_type?.id ?? singleLocationType?.id;
        if (locationType) {
            payload.location_type = String(locationType);
        }

        const rawLocationNumber = values.location_number;
        if (rawLocationNumber != null && String(rawLocationNumber).trim() !== '') {
            const parsedNumber = Number(rawLocationNumber);
            if (Number.isInteger(parsedNumber) && parsedNumber > 0) {
                payload.location_number = parsedNumber;
            }
        }
    };

    const fetchAllOrders = async () => {
        setLoading(true);
        setIsFilterBox(false);
        const values = getValues();
        let page = 1;
        const allOrders: IOrderResponse[] = [];
        const maxPages = 200;

        const today = moment().format("DD-MM-YYYY");
        let startDate = values.start_date ? moment(values.start_date).format("DD-MM-YYYY") : null;
        let endDate = values.end_date ? moment(values.end_date).format("DD-MM-YYYY") : null;

        if (!startDate && !endDate) {
            startDate = today;
            endDate = today;
        } else if (startDate && !endDate) {
            endDate = startDate;
        } else if (!startDate && endDate) {
            startDate = endDate;
        }

        while (true) {
            const payload: IGetCompanyFileredData = { company_id, page };
            payload.start_date = startDate!;
            payload.end_date = endDate!;

            if (values.order_type && values.order_type.id !== "All")
                payload.order_type = values.order_type.id as string;
            if (values.order_status && values.order_status.id !== "All")
                payload.order_status = values.order_status.id as string;

            appendDineInLocationFilters(payload, values);

            try {
                const response: any = await getFilteredOrders(payload).unwrap();

                if (response.results?.length) {
                    allOrders.push(...response.results);
                }

                if (response.next) {
                    page += 1;
                    if (page > maxPages) {
                        console.warn("Stopped fetching - reached maxPages guard");
                        break;
                    }
                } else {
                    break;
                }
            } catch (err) {
                console.error("Error fetching orders:", err);
                break;
            }
        }

        setFilterdOrders(allOrders);
        setOrdersCount(allOrders.length);
        setNextPage(null);
        setLoading(false);
    };


    const onFilter = async () => {
        await fetchAllOrders();
    };

    const selectOrder = (order: IOrderResponse) => {
        setSelectedOrder(order);
        setActiveContent(CurrentOrdersContentTypes.ITEM);
    };

    const orderTypesData: IAutocompleteData = useMemo(() => {
        const options: IAutocompleteData = [
            { id: "All", title: t("My_Business.Personal.All") },
        ];
        const services = companyData?.services;

        if (!services) {
            return options;
        }

        if (services.delivery?.is_active) {
            options.push({ id: OrderTypes.DELIVERY, title: t("My_Business.My_Services.Delivery") });
        }
        if (services.pre_order) {
            options.push({ id: OrderTypes.PREORDER, title: t("My_Business.My_Services.Pre_Order") });
        }
        if (services.reserve) {
            options.push({ id: OrderTypes.RESERVE, title: t("My_Business.My_Services.Reserve") });
        }
        if (services.dine_in?.is_active) {
            options.push({ id: OrderTypes.DINE_IN, title: t("My_Business.My_Services.Dine_In.Title") });
        }

        return options;
    }, [companyData?.services, t]);

    const enabledOrderTypes = useMemo(
        () => orderTypesData.filter((option) => option.id !== "All"),
        [orderTypesData],
    );

    const defaultOrderType = useMemo(
        () => (enabledOrderTypes.length === 1 ? enabledOrderTypes[0] : orderTypesData[0]),
        [enabledOrderTypes, orderTypesData],
    );

    const applyDineInLocationDefaults = () => {
        if (singleLocationType) {
            setValue("location_type", singleLocationType);
        }
    };

    const clearDineInLocationFields = () => {
        setValue("location_type", undefined);
        setValue("location_number", undefined);
    };

    const applyOrderTypeSelection = (orderType: IAutocompleteData[number]) => {
        setValue("order_type", orderType);

        if (orderType.id === OrderTypes.DINE_IN) {
            applyDineInLocationDefaults();
        } else {
            clearDineInLocationFields();
        }
    };

    useEffect(() => {
        if (!companyData?.services) {
            return;
        }

        const currentOrderType = getValues("order_type");
        const isValidSelection = Boolean(
            currentOrderType && orderTypesData.some((option) => option.id === currentOrderType.id),
        );
        const shouldUseSingleServiceDefault =
            enabledOrderTypes.length === 1 &&
            (!currentOrderType || currentOrderType.id === "All");

        if (!isValidSelection || shouldUseSingleServiceDefault) {
            applyOrderTypeSelection(defaultOrderType);
        }
    }, [
        companyData?.services,
        defaultOrderType,
        enabledOrderTypes.length,
        getValues,
        orderTypesData,
        setValue,
        singleLocationType,
    ]);

    const orderStatusesData: IAutocompleteData = [
        { id: "All", title: t("My_Business.Personal.All") },
        { id: OrderStatus.ACCEPTED, title: t("My_Business.OrderStatus.accepted") },
        { id: OrderStatus.DELIVERED, title: t("My_Business.OrderStatus.delivered") },
        { id: OrderStatus.PENDING, title: t("My_Business.OrderStatus.pending") },
        { id: OrderStatus.CANCELLED, title: t("My_Business.OrderStatus.cancelled") },
        { id: OrderStatus.REJECTED, title: t("My_Business.OrderStatus.rejected") },
        { id: OrderStatus.IN_PREPARATION, title: t("My_Business.OrderStatus.in_preparation") },
        { id: OrderStatus.READY_FOR_PICKUP, title: t("My_Business.OrderStatus.ready_for_pickup") },
        { id: OrderStatus.OUT_FOR_DELIVERY, title: t("My_Business.OrderStatus.out_for_delivery") },
    ];

    const onReset = () => {
        applyOrderTypeSelection(defaultOrderType);
        setValue("order_status", orderStatusesData[0]);
        setValue("end_date", undefined);
        setValue("start_date", undefined);
        setValue("order_id", undefined);
        if (defaultOrderType.id !== OrderTypes.DINE_IN) {
            clearDineInLocationFields();
        } else {
            setValue("location_number", undefined);
            applyDineInLocationDefaults();
        }
        setFilterdOrders([]);
        setOrdersCount(0);
    };

    const onOrderTypeChange = (value: IAutocompleteData[number] | null) => {
        if (!value) {
            return;
        }

        applyOrderTypeSelection(value);
    };

    useEffect(() => {
        setValue("order_status", orderStatusesData[0]);
    }, []);

    const onCloseDialog = () => {
        setDialogComponent(null);
        onClose();
    };

    const onOpenStatusDialog = (
        order: IOrderResponse,
        setLocalStatus?: React.Dispatch<React.SetStateAction<OrderStatus>>,
        currentStatus?: OrderStatus
    ) => {
        setDialogComponent(
            <OrderStatusDialog
                company_id={company_id}
                current_status={currentStatus ?? order.status}
                order_id={order.id}
                onClose={onCloseDialog}
                orderType={order.order_type}
                setLocalStatus={setLocalStatus}
            />
        );
        onOpen();
    };

    const onOpenPrintInvoceDialog = (order: IOrderResponse) => {
        setDialogComponent(<PrintInvoiceDialog order={order} QR={QR} onClose={onCloseDialog} />);
        onOpen();
    };

    const onBack = () => {
        setSelectedOrder(null);
        setActiveContent(CurrentOrdersContentTypes.LIST);
    };

    const hasActiveOrders = useMemo(() => filterdOrders.length > 0, [filterdOrders]);

    return {
        errors,
        loading,
        control,
        isFilterBox,
        ordersCount,
        isDialogOpen,
        activeContent,
        filterdOrders,
        selectedOrder,
        orderTypesData,
        dialogComponent,
        hasActiveOrders,
        orderStatusesData,
        isDineInFilter,
        dineInLocationTypes,
        hasMultipleLocationTypes,
        singleLocationType,
        onBack,
        onReset,
        register,
        setValue,
        onFilter,
        selectOrder,
        onSearchById,
        onCloseDialog,
        onOrderTypeChange,
        onOpenFilterBox: () => setIsFilterBox(!isFilterBox),
        setActiveContent,
        onOpenStatusDialog,
        onOpenPrintInvoceDialog,
    };
};

export default useOrdersHistory;
